<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, Trello, Columns3, CheckCircle2 } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface KanbanTask {
  id: string
  desc: string
  assigned: string
  priority: string
}

interface KanbanSection {
  id: string
  title: string
  tasks: KanbanTask[]
}

const chartTitle = ref('Desarrollo del Proyecto')
const sections = ref<KanbanSection[]>([
  {
    id: crypto.randomUUID(),
    title: 'Por Hacer (To Do)',
    tasks: [
      { id: 't1', desc: 'Diseñar arquitectura', assigned: 'Alice', priority: 'High' }
    ]
  },
  {
    id: crypto.randomUUID(),
    title: 'En Progreso',
    tasks: [
      { id: 't2', desc: 'Configurar servidor', assigned: 'Bob', priority: 'Medium' }
    ]
  },
  {
    id: crypto.randomUUID(),
    title: 'Hecho (Done)',
    tasks: [
      { id: 't3', desc: 'Reunión inicial', assigned: 'Team', priority: 'Low' }
    ]
  }
])

const generateId = () => crypto.randomUUID()

const addSection = () => {
  sections.value.push({
    id: generateId(),
    title: `Nueva Columna`,
    tasks: []
  })
}

const removeSection = (idx: number) => {
  sections.value.splice(idx, 1)
}

const addTask = (secIdx: number) => {
  const newId = `task_${Math.floor(Math.random() * 10000)}`
  sections.value[secIdx].tasks.push({
    id: newId,
    desc: 'Nueva Tarea',
    assigned: '',
    priority: 'Normal'
  })
}

const removeTask = (secIdx: number, taskIdx: number) => {
  sections.value[secIdx].tasks.splice(taskIdx, 1)
}

// Generar código Mermaid
const generateMermaid = () => {
  let code = `kanban\n`
  if (chartTitle.value.trim()) {
    code += `  title ${chartTitle.value.trim()}\n`
  }
  
  sections.value.forEach(sec => {
    const secTitle = sec.title.trim() || 'Columna'
    code += `  section ${secTitle}\n`
    
    sec.tasks.forEach(t => {
      const safeId = t.id.replace(/[^a-zA-Z0-9_]/g, '') || `id_${Math.floor(Math.random()*1000)}`
      const safeDesc = t.desc.trim().replace(/\[|\]/g, '') || 'Tarea'
      let line = `    ${safeId}[${safeDesc}]`
      
      const meta = []
      if (t.assigned) meta.push(`assigned: '${t.assigned.trim()}'`)
      if (t.priority && t.priority !== 'Normal') meta.push(`priority: '${t.priority.trim()}'`)
      
      if (meta.length > 0) {
        line += `@{ ${meta.join(', ')} }`
      }
      code += `${line}\n`
    })
  })
  
  editableCode.value = code
}

// Parsear desde Mermaid
const syncToVisual = () => {
  const lines = editableCode.value.split('\n')
  const newSections: KanbanSection[] = []
  let newTitle = ''
  
  let currentSec: KanbanSection | null = null
  
  for (const line of lines) {
    const t = line.trim()
    if (!t || t === 'kanban') continue
    
    if (t.startsWith('title ')) {
      newTitle = t.replace('title ', '').trim()
    } else if (t.startsWith('section ')) {
      currentSec = { id: generateId(), title: t.replace('section ', '').trim(), tasks: [] }
      newSections.push(currentSec)
    } else if (currentSec && t.match(/^([a-zA-Z0-9_]+)\[([^\]]+)\]/)) {
      const match = t.match(/^([a-zA-Z0-9_]+)\[([^\]]+)\]/)
      if (match) {
        const taskId = match[1]
        const desc = match[2]
        
        let assigned = ''
        let priority = 'Normal'
        
        const metaMatch = t.match(/@\{\s*(.+?)\s*\}/)
        if (metaMatch) {
          const metaStr = metaMatch[1]
          
          const assignedMatch = metaStr.match(/assigned:\s*'([^']+)'/)
          if (assignedMatch) assigned = assignedMatch[1]
          
          const priorityMatch = metaStr.match(/priority:\s*'([^']+)'/)
          if (priorityMatch) priority = priorityMatch[1]
        }
        
        currentSec.tasks.push({
          id: taskId,
          desc,
          assigned,
          priority
        })
      }
    }
  }
  
  if (newTitle) chartTitle.value = newTitle
  if (newSections.length > 0) sections.value = newSections
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([chartTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Tablero Kanban"
    description="Organiza tus tareas en columnas para visualizar el flujo de trabajo (Workflow)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <Trello class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" variant="default" @click="addSection"><Columns3 class="w-4 h-4 mr-2" /> Añadir Columna</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Título -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2">
          <h3 class="font-semibold text-lg text-primary">Nombre del Tablero</h3>
          <input 
            v-model="chartTitle" 
            class="bg-background border rounded px-3 py-2 text-base font-bold focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-1/2"
            placeholder="Ej. Proyecto Alpha"
          />
        </div>

        <!-- Columnas (Secciones) -->
        <div class="flex overflow-x-auto pb-4 gap-4 items-start snap-x snap-mandatory">
          
          <div v-if="sections.length === 0" class="w-full text-center text-muted-foreground py-8 border-2 border-dashed rounded-lg">
            Tablero vacío. Añade una columna para empezar.
          </div>

          <div 
            v-for="(sec, sIdx) in sections" 
            :key="sec.id"
            class="bg-card border rounded-lg shadow-sm min-w-[300px] max-w-[320px] flex flex-col snap-center shrink-0"
          >
            <!-- Cabecera Columna -->
            <div class="p-3 border-b flex items-center justify-between bg-muted/30">
              <input 
                v-model="sec.title" 
                class="bg-transparent font-bold text-sm focus:outline-none focus:border-b border-primary w-2/3"
                placeholder="Nombre Columna"
              />
              <button 
                @click="removeSection(sIdx)" 
                class="text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded transition-colors"
                title="Eliminar Columna"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>

            <!-- Lista de Tareas -->
            <div class="p-3 flex flex-col gap-3 min-h-[100px]">
              <div 
                v-for="(task, tIdx) in sec.tasks" 
                :key="task.id"
                class="bg-background border shadow-sm rounded p-3 flex flex-col gap-2 relative group hover:border-primary/50 transition-colors"
              >
                <button 
                  @click="removeTask(sIdx, tIdx)" 
                  class="absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-all"
                  title="Eliminar Tarea"
                >
                  <Trash2 class="w-3 h-3" />
                </button>

                <!-- ID Tarea -->
                <input 
                  v-model="task.id" 
                  class="bg-transparent text-[10px] font-mono text-muted-foreground uppercase focus:outline-none focus:border-b w-1/3"
                  placeholder="ID_TAREA"
                />

                <!-- Descripción -->
                <textarea
                  v-model="task.desc"
                  class="bg-transparent text-sm focus:outline-none resize-none font-medium w-full mt-1"
                  placeholder="Descripción de la tarea..."
                  rows="2"
                ></textarea>

                <!-- Metadatos -->
                <div class="flex items-center gap-2 mt-2 pt-2 border-t border-dashed">
                  <div class="flex flex-col w-1/2">
                    <label class="text-[9px] font-bold text-muted-foreground uppercase">Asignado a</label>
                    <input v-model="task.assigned" class="bg-transparent text-xs focus:outline-none" placeholder="@usuario" />
                  </div>
                  <div class="flex flex-col w-1/2">
                    <label class="text-[9px] font-bold text-muted-foreground uppercase">Prioridad</label>
                    <select v-model="task.priority" class="bg-transparent text-xs focus:outline-none cursor-pointer">
                      <option value="Normal">Normal</option>
                      <option value="High">Alta (High)</option>
                      <option value="Medium">Media (Medium)</option>
                      <option value="Low">Baja (Low)</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Añadir Tarea Botón -->
              <button 
                @click="addTask(sIdx)"
                class="w-full flex items-center justify-center gap-2 py-2 mt-2 text-xs font-semibold text-muted-foreground hover:text-primary hover:bg-muted rounded border border-dashed transition-colors"
              >
                <Plus class="w-3 h-3" /> Añadir Tarea
              </button>
            </div>
          </div>

        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
